import { createContext, useContext, type ReactNode } from "react";
import { ArtifactCardActionsProvider } from "~/pages/manifold-nextgen/components/conversations/artifacts/ArtifactCardActions";
import {
  SessionContextPanelType,
  useSessionContextPanel,
} from "~/pages/manifold-nextgen/hooks/useSessionContextPanel";
import type { ArtifactPreview } from "~/services/artifacts";
import { useArtifactSharing } from "./useArtifactSharing";

export interface SessionArtifactsScopeValue {
  // The canonical UUID, which every pin call keys on.
  projectUuid: string;
  conversationId: string;
  // Whether the caller may share to the project: curating is owner-only.
  canShare: boolean;
}

const SessionArtifactsScopeContext = createContext<SessionArtifactsScopeValue | null>(null);

interface SessionArtifactsScopeProps {
  projectUuid: string;
  // Unset off a session route, where cards get no actions.
  conversationId: string | undefined;
  canShare: boolean;
  children: ReactNode;
}

/** Wires a session's artifact cards to its side panel and its project.
 *
 * Mounted by `ProjectLayout` around both the routed `Chat` and the sidebar,
 * which sit outside each other, so a card's Lineage button can open the
 * sidebar's artifact panel and both agree on which artifacts are shared.
 * Always mounted, session or not: swapping the tree around the outlet would
 * remount the routed conversation.
 */
export const SessionArtifactsScope = ({
  projectUuid,
  conversationId,
  canShare,
  children,
}: SessionArtifactsScopeProps) => {
  const { panel, openPanel, expandSidebar } = useSessionContextPanel();
  const sharing = useArtifactSharing(conversationId ? projectUuid : null, conversationId, canShare);

  const resolve = (preview: ArtifactPreview) => {
    const name = preview.artifact_name;
    if (name === null) return { lineage: null, share: null };
    const toggle = sharing.toggle;
    return {
      lineage: {
        isOpen: panel?.type === SessionContextPanelType.ARTIFACT && panel.id === name,
        open: () => {
          openPanel(SessionContextPanelType.ARTIFACT, name);
          expandSidebar();
        },
      },
      share: toggle && {
        isShared: sharing.pinFor(name) !== null,
        isReady: sharing.isReady,
        toggle: () => void toggle(name, preview.summary ?? name),
      },
    };
  };

  return (
    <SessionArtifactsScopeContext.Provider
      value={conversationId ? { projectUuid, conversationId, canShare } : null}
    >
      <ArtifactCardActionsProvider resolve={conversationId ? resolve : null}>
        {children}
      </ArtifactCardActionsProvider>
    </SessionArtifactsScopeContext.Provider>
  );
};

/** The session in scope for its artifacts, or null outside one. */
export const useSessionArtifactsScope = (): SessionArtifactsScopeValue | null =>
  useContext(SessionArtifactsScopeContext);
